import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { withStyles } from "@material-ui/core/styles";
import { Dialog, DialogActions, DialogTitle, Grid, Typography, WithStyles } from "@material-ui/core";
import { styles } from "./ManageUsersView.styles";
import * as UserDetailsAction from "../../../stores/user/UserDetailsAction";
import UniversalTable from "../../components/UniversalTable/UniversalTable";
import { IUniversalTableColumn, IUniversalTableData } from "../../components/UniversalTable/interfaces";
import { useSnackbar } from "notistack";
import ToastStatusEnum from "../../../constants/ToastStatusEnum";
import { useTranslation } from "react-i18next";
import { selectRequesting } from "../../../selectors/requesting/RequestingSelector";
import { Breadcrumbs } from "../../components/Breadcrumbs/Breadcrumbs";
import { useHistory } from "react-router-dom";
import UserDetailsModel from "../../../models/user/UserDetailsModel";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import DateRangeIcon from "@material-ui/icons/DateRange";
import EditIcon from "@material-ui/icons/Edit";
import { CsvExportButton } from "../../components/CsvExport/CsvExportButton";
import { convertDateToFullDateTime, today } from "../../../utilities/dateTimeUtils";
import { BlueButton } from "../../components/Buttons/BlueButton";
import { CenteredContainer } from "../../components/CenteredContainer/CenteredContainer";
import PermissionGuard from "../../components/PermissionGuard/PermissionGuard";
import LockOutlinedIcon from "@material-ui/icons/LockOutlined";
import NoEncryptionOutlinedIcon from "@material-ui/icons/NoEncryptionOutlined";
import { ManageUsersFilter } from "../../components/ManageUsersFilter/ManageUsersFilter";
import FingerprintIcon from "@material-ui/icons/Fingerprint";

export interface IManageUsersViewProps extends WithStyles<typeof styles> {
}

const ManageUsersViewPure: React.FunctionComponent<IManageUsersViewProps> = ({classes}: IManageUsersViewProps) => {
    const dispatch = useDispatch();
    const {enqueueSnackbar} = useSnackbar();
    const {t} = useTranslation();
    const history = useHistory();

    const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
    const [userIndexToBeReset, setUserIndexToBeReset] = useState(-1);
    const [userTypesChecked, setUserTypesChecked] = useState<string[]>([]);
    const [activeChecked, setActiveChecked] = useState<string[]>([]);
    const [lockedChecked, setLockedChecked] = useState<string[]>([]);

    useEffect(() => {
        dispatch(UserDetailsAction.requestAllUsersDetails());
    }, [dispatch]);

    const allUsersFromState = useSelector(state => state.userDetails?.all);
    const isRequesting = useSelector(state => selectRequesting(state, [UserDetailsAction.REQUEST_ALL_USERS_DETAILS]));

    // internal user doesn't have field username
    const isUserInternal = (user) => user.username === "";

    const isUserFiltered = (userProperty, filter1, filter2) => {
        return (!userProperty && !filter1) || (filter1 && filter2) || (userProperty && !filter2);
    };

    const filterUsers = (users, typesChecked, activeChecked, lockedChecked): UserDetailsModel[] => {
        return users.filter(user => isUserFiltered(isUserInternal(user), typesChecked.includes("Internal"), typesChecked.includes("External")))
            .filter(user => isUserFiltered(user.accountActivated, activeChecked.includes("Active"), activeChecked.includes("Inactive")))
            .filter(user => isUserFiltered(user.userLocked, lockedChecked.includes("Locked"), lockedChecked.includes("Not locked")));
    };

    let users: IUniversalTableData;
    if (allUsersFromState) {
        let relevantlUsers: UserDetailsModel[] = Object.values(allUsersFromState);
        relevantlUsers = filterUsers(relevantlUsers, userTypesChecked, activeChecked, lockedChecked);
        users = relevantlUsers?.map((user: any) => {
            const actionsDisabled = [
                ...(isUserInternal(user) ? ["lockUnlock"] : []),
                // enabled only for non-activated external users
                ...(!(!isUserInternal(user) && !user.accountActivated) ? ["resetPassword"] : []),
                // enabled only for external deletable users
                ...(!(!isUserInternal(user) && user.isDeletable) ? ["delete"] : [])
            ];
            return {
                userId: user.id,
                firstName: user.firstName,
                lastName: user.lastName,
                email: user.email,
                additionalEmail2: user.additionalEmail2 || "",
                additionalEmail3: user.additionalEmail3 || "",
                company: user.company,
                bpId: user.bpId,
                bpName: user.bpName,
                createdOn: user.createdOn,
                accountActivated: user.accountActivated,
                actionsDisabled,
                isUserLocked: user.userLocked,
                userName: user.username,
                isInternalUser: isUserInternal(user),
                isDeletable: user.isDeletable
            };
        });
    }

    const handleUserEdit = index => {
        const path = "/admin/manage-users/" + users[index].userId;
        history.push(path);
    };

    const handleUserDelete = index => {
        if (users[index]?.isDeletable) {
            dispatch(UserDetailsAction.deleteUser(users[index].userId)).then(() => {
                dispatch(UserDetailsAction.requestAllUsersDetails());
                enqueueSnackbar("User deleted", {
                    variant: ToastStatusEnum.Info
                });
            })
            .catch(error =>
                enqueueSnackbar("User deletion failed") + ": " + error, {
                    variant: ToastStatusEnum.Error
                })
        } 
    };

    const handleManageOrders = index => {
        const path = "/admin/manage-users/" + users[index].userId + "/orders";
        history.push(path);
    };
    const handleManageSubscriptions = index => {
        const path = "/admin/manage-users/" + users[index].userId + "/subscriptions";
        history.push(path);
    };

    const handleResetPassword = index => {
        dispatch(UserDetailsAction.resetPassword(users[index].userId)).then(() => {
            enqueueSnackbar("Password has been reset for " + users[index].email + "", {
                variant: ToastStatusEnum.Info
            });
        });
        setUserIndexToBeReset(-1);
        setConfirmationDialogOpen(false);
    };

    const handleResetPasswordDialog = index => {
        setUserIndexToBeReset(index);
        setConfirmationDialogOpen(true);
    };

    const handleLockUnlock = index => {
        if (users[index]?.isUserLocked) {
            dispatch(UserDetailsAction.unlockUser(users[index].userId)).then(() => {
                dispatch(UserDetailsAction.requestAllUsersDetails());
                enqueueSnackbar("User unlocked", {
                    variant: ToastStatusEnum.Info
                });
            });
        } else {
            dispatch(UserDetailsAction.lockUser(users[index].userId)).then(() => {
                dispatch(UserDetailsAction.requestAllUsersDetails());
                enqueueSnackbar("User locked", {
                    variant: ToastStatusEnum.Info
                });
            });
        }
    };

    const handleAddMultipleSubscriptions = () => {
        const path = "/admin/manage-users/add-multiple-subscriptions";
        history.push(path);
    };

    const handleRegistrationOnBehalf = () => {
        const path = "/admin/manage-users/register-on-behalf";
        history.push(path);
    };

    const handleOderTypeChange = (event: React.MouseEvent<HTMLElement>, newTypes: string[]) => {
        setUserTypesChecked(newTypes);
    };

    const handleActiveChange = (event: React.MouseEvent<HTMLElement>, newActive: string[]) => {
        setActiveChecked(newActive);
    };

    const handleLockedChange = (event: React.MouseEvent<HTMLElement>, newLocked: string[]) => {
        setLockedChecked(newLocked);
    };

    const columns: IUniversalTableColumn[] = [
        {
            type: "link",
            title: t("general.email"),
            field: "email",
            onClick: index => handleUserEdit(index)
        },
        {
            type: "text",
            title: t("general.firstName"),
            field: "firstName"
        },
        {
            type: "text",
            title: t("general.lastName"),
            field: "lastName"
        },
        {
            type: "text",
            title: "Company name & Legal form",
            field: "company"
        },
        {
            type: "dateTime",
            title: t("general.createdOn"),
            field: "createdOn"
        },
        {
            type: "boolean",
            title: t("admin.manageUsersView.accountActivated"),
            field: "accountActivated"
        },
        {
            type: "boolean",
            title: "User type",
            field: "isInternalUser",
            trueText: "Internal",
            falseText: "External"
        },
        {
            type: "actions",
            title: t("admin.taskView.actions"),
            actions: [
                {
                    name: "manageOrders",
                    icon: <FileCopyIcon />,
                    tooltip: t("admin.manageOrdersView.manageOrders"),
                    onClick: index => handleManageOrders(index)
                },
                {
                    name: "manageSubscriptions",
                    icon: <DateRangeIcon />,
                    tooltip: t("admin.manageSubscriptionsView.manageSubscriptions"),
                    onClick: index => handleManageSubscriptions(index),
                    scope: ["subscriptions.users.write", "subscriptions.users.read"]
                },
                {
                    name: "editUser",
                    icon: <EditIcon />,
                    tooltip: t("general.edit"),
                    onClick: index => handleUserEdit(index),
                    scope: "users.write"
                },
                {
                    name: "resetPassword",
                    icon: <FingerprintIcon />,
                    tooltip: "Reset password",
                    onClick: index => handleResetPasswordDialog(index),
                    disabledTooltip: "User already activated",
                    scope: "users.write"
                },
                {
                    name: "lockUnlock",
                    twoStatesAction: true,
                    stateField: "isUserLocked",
                    trueIcon: <NoEncryptionOutlinedIcon />,
                    falseIcon: <LockOutlinedIcon />,
                    trueTooltip: "Unlock user",
                    falseTooltip: "Lock user",
                    disabledTooltip: "Can't lock/unlock internal user",
                    tooltip: "Lock/Unlock user",
                    onClick: index => handleLockUnlock(index),
                    scope: "users.write"
                },
                {
                    name: "delete",
                    disabledTooltip: "The user can't be deleted",
                    deleteQuestion: "Do you want to delete this user?",
                    tooltip: "Delete user",
                    scope: "users.write",
                    onDeleteFinished: (index) => handleUserDelete(index)
                }
            ]
        }
    ];

    const users_mapping = [
        {
            label: "Email",
            key: "email"
        },
        {
            label: "Email 2",
            key: "additionalEmail2",
        },
        {
            label: "Email 3",
            key: "additionalEmail3"
        },
        {
            label: "User name",
            key: "userName"
        },
        {
            label: "First Name",
            key: "firstName"
        },
        {
            label: "Last Name",
            key: "lastName"
        },
        {
            label: "Company name & Legal form",
            key: "company"
        },
        {
            key: "bpId",
            label: "SAP BP-ID",
        },
        {
            key: "bpName",
            label: "SAP BP-Name",
        },
        {
            label: "Created on",
            key: "createdOn",
            mappingFunc: (value) => convertDateToFullDateTime(value)
        },
        {
            label: "Account activated",
            key: "accountActivated",
            mappingFunc: (value) => value ? "Yes" : "No"
        },
        {
            label: "User locked",
            key: "isUserLocked",
            mappingFunc: (value) => value ? "Yes" : "No"
        },
        {
            label: "User type",
            key: "isInternalUser",
            mappingFunc: (value) => value ? "Internal" : "External"
        }
    ];

    return (
        <CenteredContainer>
            <Breadcrumbs
                items={[
                    {title: t("admin.adminPanel"), href: "/admin"},
                    {title: t("admin.manageUsersView.manageUsers")}
                ]}
            />
            <div className={classes.title}>
                <Typography variant="h3">{t("admin.manageUsersView.manageUsers")}</Typography>
            </div>
            <PermissionGuard scope="users.write">
                <div>
                    <Grid container direction="column">
                        <Grid item>
                            <BlueButton onClick={handleRegistrationOnBehalf}>Register user on behalf</BlueButton>
                        </Grid>
                        <Grid item className={classes.buttonMargin}>
                            <BlueButton onClick={handleAddMultipleSubscriptions}>Add multiple subscriptions</BlueButton>
                        </Grid>
                    </Grid>
                </div>
            </PermissionGuard>
            <CsvExportButton
                data={users ? users : []}
                outputFilename={`webshop_users_${today().format("YYYYMMDD")}.csv`}
                mappings={users_mapping}
            >
                {t("admin.manageUsersView.exportUsers")}
            </CsvExportButton>
            <ManageUsersFilter
                userTypesChecked={userTypesChecked}
                onUserTypeCheckboxClick={handleOderTypeChange}
                activeChecked={activeChecked}
                onActiveCheckboxClick={handleActiveChange}
                lockedChecked={lockedChecked}
                onLockedCheckboxClick={handleLockedChange}
            />
            <div className={classes.userTable}>
                <UniversalTable columns={columns} data={users} isLoading={isRequesting} searchable />

                <Dialog open={confirmationDialogOpen}>
                    <DialogTitle>Are you sure you want to reset the password?</DialogTitle>
                    <DialogActions>
                        <BlueButton
                            onClick={() => handleResetPassword(userIndexToBeReset)}
                        >
                            {t("general.yes")}
                        </BlueButton>
                        <BlueButton
                            onClick={() => setConfirmationDialogOpen(false)}
                        >
                            {t("general.no")}
                        </BlueButton>
                    </DialogActions>
                </Dialog>
            </div>
        </CenteredContainer>
    );
};

export const ManageUsersView = withStyles(styles)(ManageUsersViewPure);
